import styles from "./DashboardPage.module.scss";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { PlusCircleFill } from "src/assets/icons";
import { Button, Preloader } from "src/components";
import { ROUTES } from "src/constants";
import { useDashboardTopicsLoader } from "src/containers/Home/hooks/mainAppDataLoaders";
import { DashboardSidebar, WidgetsSidebar } from "src/features";
import { useQueryParamsState } from "src/hooks";
import { DashboardPageContext } from "src/pages/Dashboards/DashboardPageWrapper/context";
import { useAppDispatch } from "src/store";
import { showToastNotification } from "src/utils";
import { OPEN_BUTTON_ID } from "./constants";

import {
  updateUserLastViewedDashboardIds,
  fetchTrackersByIds,
} from "src/store/actions";
import {
  selectActiveWidgets,
  selectIsCompanyDashboard,
  selectAvailableDashboardById,
  selectTrackersCollectionById,
  selectTrackersEntities,
} from "src/store/selectors";

// Inner imports
import {
  DashboardHead,
  DashboardBody,
  DashboardConfiguration,
  DashboardTrackersAlert,
} from "./components";
import {
  useDashboardWidgetSyncs,
  useDashboardSearchesLoader,
  useAvailableDashboardLoader,
  useDashboardDateRangesLoader,
  useTrackersCollectionObserver,
  useDashboardWidgetSyncsObserver,
  useDefaultDashboardDateRangeWidgetsDataLoader,
} from "./hooks";

type Props = {
  isReadOnly?: boolean;
  isUpdateAllowed?: boolean;
  dashboardId: Dashboard.Data["id"];
};

const DashboardPage: FC<Props> = ({
  dashboardId,
  isUpdateAllowed = false,
  isReadOnly: _isReadOnly,
}) => {
  const { t } = useTranslation();

  const history = useHistory();

  const { setSidebarContent } = useContext(DashboardPageContext);

  const { state } = useLocation<LocationState>();

  const dispatch = useAppDispatch();

  const activeWidgets = useSelector(selectActiveWidgets);

  const isCompanyDashboard = useSelector((state: Store.RootState) =>
    selectIsCompanyDashboard(state, dashboardId),
  );

  const isReadOnly = useMemo<boolean>(
    () => _isReadOnly ?? state?.isReadOnly ?? false,
    [_isReadOnly, state?.isReadOnly],
  );

  const dashboard = useSelector((state: Store.RootState) =>
    selectAvailableDashboardById(state, dashboardId, isReadOnly),
  );

  const trackersCollection = useSelector((state: Store.RootState) =>
    selectTrackersCollectionById(state, dashboardId),
  );

  const { tiles, layoutView } = useMemo(
    () => ({
      tiles: dashboard?.tiles || {},
      layoutView: dashboard?.layoutView || "full",
    }),
    [dashboard],
  );

  const trackerIds = useMemo<Tracker.Data["id"][]>(
    () => trackersCollection?.trackerIds || [],
    [trackersCollection?.trackerIds],
  );

  const [isTrackersCollectionObserverSet] = useTrackersCollectionObserver(
    dashboard?.trackersCollectionId,
    isReadOnly,
  );

  const { loadingStatus: dashboardDateRangesLoadingStatus } =
    useDashboardDateRangesLoader({ trackersCollectionId: dashboardId });

  const { loadingStatus: dashboardSearchesLoadingStatus } =
    useDashboardSearchesLoader({ trackersCollectionId: dashboardId });

  const { loadingStatus: defaultDashboardDateRangeWidgetsDataLoadingStatus } =
    useDefaultDashboardDateRangeWidgetsDataLoader({
      trackersCollectionId: dashboardId,
    });

  const { loadingStatus: availableDashboardLoadingStatus } =
    useAvailableDashboardLoader({ dashboardId, isReadOnly });

  // Load all topics for this dashboard
  const { loadingStatus: dashboardTopicsLoadingStatus } =
    useDashboardTopicsLoader(dashboardId);

  useDashboardWidgetSyncsObserver(dashboardId);

  useDashboardWidgetSyncs(dashboardId);

  const [expandedWidgetId, setExpandedWidgetId] =
    useState<Widget.IdType | null>(null);

  const [activeTrackerIds, setActiveTrackerIds] =
    useState<Tracker.Data["id"][]>(trackerIds);

  const [queryParams, setQueryParams] = useQueryParamsState();

  // Manual retry mechanism for loading trackers if needed
  const [manualRetryCount, setManualRetryCount] = useState(0);
  const maxManualRetries = 3;
  const manualRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get trackers from Redux store
  const reduxTrackers = useSelector(selectTrackersEntities);

  // Check if trackers are actually loaded in Redux store
  const actuallyLoadedTrackerIds = useMemo(() => {
    return trackerIds.filter((id) => Boolean(reduxTrackers[id]));
  }, [trackerIds, reduxTrackers]);

  // Try to directly load trackers if they're missing but we think they should be loaded
  useEffect(() => {
    if (
      dashboardTopicsLoadingStatus === "succeeded" &&
      trackerIds.length > 0 &&
      actuallyLoadedTrackerIds.length === 0 &&
      manualRetryCount < maxManualRetries
    ) {
      // Clear any existing timeout
      if (manualRetryTimeoutRef.current) {
        clearTimeout(manualRetryTimeoutRef.current);
      }

      // Set a new timeout to retry loading trackers
      manualRetryTimeoutRef.current = setTimeout(() => {
        dispatch(fetchTrackersByIds(trackerIds))
          .unwrap()
          .then(() => {
            setManualRetryCount((count) => count + 1);
          })
          .catch(() => {
            setManualRetryCount((count) => count + 1);
          });
      }, 1000);
    }

    // Cleanup timeout
    return () => {
      if (manualRetryTimeoutRef.current) {
        clearTimeout(manualRetryTimeoutRef.current);
      }
    };
  }, [
    dashboardTopicsLoadingStatus,
    trackerIds,
    actuallyLoadedTrackerIds.length,
    manualRetryCount,
    dispatch,
  ]);

  // const isTopicsLoaded = useMemo(() => {
  //   // If no tracker IDs, consider it loaded
  //   if (trackerIds.length === 0) return true;

  //   // If we have at least one tracker loaded and loading status is done, consider it loaded
  //   const hasLoadedTrackers = actuallyLoadedTrackerIds.length > 0;
  //   const isLoadingComplete =
  //     dashboardTopicsLoadingStatus === "succeeded" ||
  //     dashboardTopicsLoadingStatus === "failed";

  //   return isLoadingComplete && hasLoadedTrackers;
  // }, [dashboardTopicsLoadingStatus, trackerIds, actuallyLoadedTrackerIds]);

  const dashboardDateRangeId = useMemo<DashboardDateRange.Data["id"]>(
    () => queryParams.dateRangeId || "",
    [queryParams.dateRangeId],
  );

  useEffect(() => {
    if (!isCompanyDashboard) return;

    dispatch(
      updateUserLastViewedDashboardIds({
        lastViewedDashboardIds: [dashboardId],
      }),
    )
      .unwrap()
      .catch();
  }, [dispatch, isCompanyDashboard, dashboardId]);

  useEffect(() => {
    if (!dashboardId) return;

    setExpandedWidgetId(null);
  }, [dashboardId]);

  useEffect(() => {
    if (trackerIds.length) setActiveTrackerIds(trackerIds);
  }, [trackerIds]);

  const isDashboardDateRangesLoaded = useMemo<boolean>(
    () =>
      dashboardDateRangesLoadingStatus === "succeeded" ||
      dashboardDateRangesLoadingStatus === "failed",
    [dashboardDateRangesLoadingStatus],
  );

  const isDashboardSearchesLoaded = useMemo<boolean>(
    () =>
      dashboardSearchesLoadingStatus === "succeeded" ||
      dashboardSearchesLoadingStatus === "failed",
    [dashboardSearchesLoadingStatus],
  );

  const isDefaultDashboardDateRangeWidgetsDataLoaded = useMemo<boolean>(
    () =>
      defaultDashboardDateRangeWidgetsDataLoadingStatus === "succeeded" ||
      defaultDashboardDateRangeWidgetsDataLoadingStatus === "failed",
    [defaultDashboardDateRangeWidgetsDataLoadingStatus],
  );

  const isAvailableDashboardLoaded = useMemo<boolean>(
    () =>
      availableDashboardLoadingStatus === "succeeded" ||
      availableDashboardLoadingStatus === "failed",
    [availableDashboardLoadingStatus],
  );

  // Force show dashboard after max retries
  const forceShowDashboard = useMemo(
    () => manualRetryCount >= maxManualRetries,
    [manualRetryCount, maxManualRetries],
  );

  const isAddWidgetButtonShown = useMemo<boolean>(() => {
    if (
      !isCompanyDashboard ||
      expandedWidgetId ||
      layoutView !== "full" ||
      isReadOnly
    )
      return false;

    for (const [widgetId] of activeWidgets) {
      const isWidgetAdded = tiles[widgetId];

      if (!isWidgetAdded) return true;
    }

    return false;
  }, [
    tiles,
    layoutView,
    isReadOnly,
    activeWidgets,
    expandedWidgetId,
    isCompanyDashboard,
  ]);

  const setDashboardDateRangeId = (
    value: DashboardDateRange.Data["id"],
  ): void => setQueryParams({ dateRangeId: value });

  const onAddWidgetButtonClick = (): void =>
    setSidebarContent(
      <WidgetsSidebar
        dashboardId={dashboardId}
        dashboardDateRangeId={dashboardDateRangeId}
      />,
    );

  const setActiveTrackerIdsHandler = (value: Tracker.Data["id"][]): void =>
    setActiveTrackerIds((state) => {
      if (value.length > 0) return value;

      showToastNotification({
        type: "info",
        id: "dashboard-tracker-min-info",
        text: t("chart.line.status.warning.minimum_trackers"),
      });

      return state;
    });

  const onSelectDashboardDateRange = (
    value: DashboardDateRange.Data["id"],
  ): void => setDashboardDateRangeId(value);

  const onEditTrackerClick = ({ id }: Tracker.Data): void =>
    history.push(`${ROUTES.trackersPage}/edit/${id}`, {
      redirectUrl: history.location.pathname,
    });

  const isStillLoading = useMemo(() => {
    const basic =
      !isAvailableDashboardLoaded ||
      !isTrackersCollectionObserverSet ||
      !isDashboardDateRangesLoaded ||
      !isDefaultDashboardDateRangeWidgetsDataLoaded ||
      !isDashboardSearchesLoaded ||
      dashboardTopicsLoadingStatus === "loading";

    // Avoid endless loading screen after max retries
    if (forceShowDashboard) return false;

    // Only check tracker loading if basic loading is complete
    return (
      basic || (trackerIds.length > 0 && actuallyLoadedTrackerIds.length === 0)
    );
  }, [
    isAvailableDashboardLoaded,
    isTrackersCollectionObserverSet,
    isDashboardDateRangesLoaded,
    isDefaultDashboardDateRangeWidgetsDataLoaded,
    isDashboardSearchesLoaded,
    dashboardTopicsLoadingStatus,
    trackerIds,
    actuallyLoadedTrackerIds,
    forceShowDashboard,
  ]);

  // Redirect if user doesn't have access to the dashboard
  if (isAvailableDashboardLoaded && !dashboard) {
    // return <Redirect to={ROUTES.dashboardsHomePage} />;
  }

  if (isStillLoading) {
    return <Preloader className={styles.preloader} />;
  }

  if (!dashboard) return <Redirect to={ROUTES.dashboardsHomePage} />;

  return (
    <div className={styles.wrapper}>
      <DashboardSidebar />
      <DashboardHead
        isReadOnly={isReadOnly}
        trackersCollectionId={dashboardId}
        setExpandedWidgetId={setExpandedWidgetId}
        dashboardDateRangeId={dashboardDateRangeId}
      />
      <DashboardConfiguration
        isReadOnly={isReadOnly}
        trackersCollectionId={dashboardId}
        activeTrackerIds={activeTrackerIds}
        dashboardDateRangeId={dashboardDateRangeId}
        setActiveTrackerIdsHandler={setActiveTrackerIdsHandler}
        selectDashboardDateRangeHandler={onSelectDashboardDateRange}
        topicsLoadingStatus={dashboardTopicsLoadingStatus}
      />
      <DashboardTrackersAlert
        isReadOnly={isReadOnly}
        trackersCollectionId={dashboardId}
        editTrackerHandler={onEditTrackerClick}
      />
      <DashboardBody
        key={dashboard.id}
        isReadOnly={isReadOnly}
        isUpdateAllowed={isUpdateAllowed}
        trackersCollectionId={dashboardId}
        expandedWidgetId={expandedWidgetId}
        activeTrackerIds={activeTrackerIds}
        setExpandedWidgetId={setExpandedWidgetId}
        dashboardDateRangeId={dashboardDateRangeId}
      />
      {isAddWidgetButtonShown && (
        <div className={styles.addWidgetButtonWrapper}>
          <Button
            buttonType="dark"
            id={OPEN_BUTTON_ID}
            onClick={onAddWidgetButtonClick}
          >
            <PlusCircleFill />
            <span>{t("page.dashboard.button.include_widget")}</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
